import { trigger, transition, style, animate } from '@angular/animations';
import {
  Component,
  ContentChildren,
  Input,
  QueryList,
  TemplateRef,
} from '@angular/core';
import { ProductStepDirective } from 'src/app/shared/directives/product-step/product-step.directive';

@Component({
  selector: 'app-product-stepper',
  templateUrl: './product-stepper.component.html',
  styleUrls: ['./product-stepper.component.scss'],
  animations: [
    trigger('slideAnimation', [
      transition(':enter', []),
      transition('* => forward', [
        style({ transform: 'translateX(100%)', opacity: 0 }),
        animate(
          '300ms ease-out',
          style({ transform: 'translateX(0)', opacity: 1 })
        ),
      ]),
      transition('* => backward', [
        style({ transform: 'translateX(-100%)', opacity: 0 }),
        animate(
          '300ms ease-out',
          style({ transform: 'translateX(0)', opacity: 1 })
        ),
      ]),
    ]),
  ],
})

/**
 * Stepper component for product add and edit page
 */
export class ProductStepperComponent {
  @ContentChildren(ProductStepDirective)
  // eslint-disable-next-line indent
  steps!: QueryList<ProductStepDirective>;

  public currentStep: number = 0;
  previousStep = 0;

  @Input() titles: string[] = ['Product Details', 'Review'];
  @Input() formValid: boolean = false;
  @Input() isEditing: boolean = false;

  /**
   * Determines the animation state based on the current and previous steps.
   * @return {'forward' | 'backward'} The animation direction.
   */
  get animationState(): 'forward' | 'backward' {
    return this.currentStep > this.previousStep ? 'forward' : 'backward';
  }

  /**
   * Retrieves the template of the current step.
   * @return {TemplateRef | null} The template reference of the current step or null if unavailable.
   */
  get currentTemplate(): TemplateRef<any> | null {
    const step = this.steps?.toArray()[this.currentStep];
    return step?.templateRef ?? null;
  }

  /**
   * Advances to the next step in the stepper
   */
  next() {
    if (this.currentStep < this.steps.length - 1) {
      this.previousStep = this.currentStep;
      this.currentStep++;
    }
  }

  /**
   * Moves to the previous step in the stepper.
   */
  prev() {
    if (this.currentStep > 0) {
      this.previousStep = this.currentStep;
      this.currentStep--;
    }
  }

  /**
   * Navigates to a specific step in the stepper.
   * @param {number} index - The index of the step to navigate to.
   */
  goToStep(index: number) {
    if (
      index >= 0 &&
      index < this.steps.length &&
      index !== this.currentStep &&
      this.formValid &&
      !this.isEditing
    ) {
      this.previousStep = this.currentStep;
      this.currentStep = index;
    }
  }
}
